import { types, flow, getEnv } from 'mobx-state-tree';

import Ad from '../models/banner/Ad';
import Error from '../models/Error';
import StatefulStore from '../models/StatefulStore';
import TextBanner from '../models/banner/TextBanner';
import RequestState, { RequestStateType } from '../types/RequestState';
import { createErrorModel } from '../util/error';
import { paramsToQueryIdentifier } from '../util/query';
import BannerPages from '../models/banner/BannerPages';

const AdStore = StatefulStore.named('AdStore')
  .props({
    adQueries: types.optional(types.map(types.array(Ad)), {}),
    adQueryStates: types.optional(types.map(RequestStateType), {}),
    adQueryError: types.maybeNull(Error),
    bannerZones: types.maybeNull(BannerPages),
    globalBanners: types.optional(types.array(Ad), []),
    textBanners: types.optional(types.array(TextBanner), []),
  })
  .volatile(() => ({
    // Live pending work is store-instance owned and not serialized.
    liveAdRequests: new Map(),
    // Query ids settled at the SSR render limit; late responses must not write.
    ssrFinalizedQueryIds: new Set(),
    // This request's final render must not start more banner work.
    isSSRFinalized: false,
  }))
  .actions((self) => {
    const fetchAds = flow(function* fetchAds(
      searchParameters,
      path,
      queryIdentifier
    ) {
      try {
        const ads = yield getEnv(self).apiWrapper.request(
          `banners${path}`,
          { params: searchParameters },
          { active_section: null }
        );
        if (self.ssrFinalizedQueryIds.has(queryIdentifier)) {
          return;
        }
        self.adQueries.set(queryIdentifier, ads);
        self.setLoading(false);
        self.adQueryStates.set(queryIdentifier, RequestState.LOADED);
      } catch (error) {
        if (self.ssrFinalizedQueryIds.has(queryIdentifier)) {
          return;
        }
        self.setError(error);
        self.adQueryError = createErrorModel(error);
        self.adQueryStates.set(queryIdentifier, RequestState.ERROR);
        throw error;
      } finally {
        if (self.liveAdRequests.get(queryIdentifier)) {
          self.liveAdRequests.delete(queryIdentifier);
        }
      }
    });

    return {
      setup: (banners) => {
        self.setBanners(banners);
      },
      setBanners: (banners) => {
        if (banners) {
          self.bannerZones = banners.bannerZones;
          self.globalBanners = banners.globalBanners;
          self.textBanners = banners.textBanners;
        }
      },
      /**
       * At the SSR render limit, settle unresolved banner queries to ERROR so
       * carousels omit skeletons. Completed results are preserved. Late API
       * responses for finalized ids are ignored.
       */
      finalizeUnresolvedQueriesForSSR() {
        self.isSSRFinalized = true;
        self.adQueryStates.forEach((state, queryIdentifier) => {
          if (state !== RequestState.LOADING) {
            return;
          }
          self.ssrFinalizedQueryIds.add(queryIdentifier);
          self.adQueryStates.set(queryIdentifier, RequestState.ERROR);
          self.liveAdRequests.delete(queryIdentifier);
        });
      },
      /**
       * Prepare banner ads for the given query. Safe for automatic render-time
       * calls: reuses cache and live requests, recovers orphaned LOADING, and
       * does not automatically retry ERROR. Pass `{ force: true }` to retry.
       */
      loadAds(searchParameters, path = '', options = {}) {
        const force = Boolean(options && options.force);
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);

        // The final pass can discover new queries. Keep cached results even
        // for forced calls, and give every unresolved query a terminal state.
        if (self.isSSRFinalized) {
          if (!self.adQueries.has(queryIdentifier)) {
            self.ssrFinalizedQueryIds.add(queryIdentifier);
            self.adQueryStates.set(queryIdentifier, RequestState.ERROR);
          }
          return Promise.resolve();
        }

        if (!force) {
          if (self.adQueries.has(queryIdentifier)) {
            return Promise.resolve();
          }
          if (self.adQueryStates.get(queryIdentifier) === RequestState.ERROR) {
            return Promise.resolve();
          }
        }

        const liveRequest = self.liveAdRequests.get(queryIdentifier);
        if (liveRequest) {
          return liveRequest;
        }

        if (force) {
          self.adQueries.delete(queryIdentifier);
        }

        let settle;
        const requestPromise = new Promise((resolve, reject) => {
          settle = { resolve, reject };
        });

        // Establish ownership before any await so concurrent callers share work.
        self.liveAdRequests.set(queryIdentifier, requestPromise);
        self.setLoading(true);
        self.adQueryStates.set(queryIdentifier, RequestState.LOADING);

        fetchAds(searchParameters, path, queryIdentifier).then(
          settle.resolve,
          settle.reject
        );

        return requestPromise;
      },
    };
  })
  .views((self) => {
    const zoneIds = (pageKey) =>
      (self.bannerZones?.[pageKey]?.zones || []).map((zone) => zone.id);

    const findZone = (pageKey, zoneId) =>
      (self.bannerZones?.[pageKey]?.zones || []).find(
        (zone) => zone.id === zoneId
      );

    return {
      shouldShowZone(pageKey, zoneId, isMobile) {
        const zone = findZone(pageKey, zoneId);
        if (!zone) {
          return false;
        }
        return isMobile ? zone.has_mobile : zone.has_non_mobile;
      },
      getZoneMeta(pageKey, zoneId) {
        return findZone(pageKey, zoneId) || null;
      },
      get campaignProductsAds() {
        return zoneIds('CAMPAIGN_PRODUCTS');
      },
      get categoryPageAds() {
        return zoneIds('CATEGORY_PAGE');
      },
      get customerProductsAds() {
        return zoneIds('CUSTOMER_PRODUCTS');
      },
      get frontPageAds() {
        return {
          zones: zoneIds('FRONT_PAGE'),
        };
      },
      get manufacturerPageAds() {
        return zoneIds('MANUFACTURER_PAGE');
      },
      get newProductsAds() {
        return zoneIds('NEW_PRODUCTS');
      },
      get popularProductsAds() {
        return zoneIds('POPULAR_PRODUCTS');
      },
      get productPageAds() {
        return zoneIds('PRODUCT_PAGE');
      },
      get recommendedProductsAds() {
        return zoneIds('RECOMMENDED_PRODUCTS');
      },
      get sectionPageAds() {
        return {
          zones: zoneIds('SECTION_FRONT_PAGE'),
        };
      },
      get soldProductsAds() {
        return zoneIds('SOLD_PRODUCTS');
      },
      getGlobalAdsByAdZone(adZone) {
        const ads = self.globalBanners;
        let adsByZone = null;
        if (ads) {
          adsByZone = ads.filter((ad) => {
            return ad.zones.indexOf(adZone) !== -1;
          });
        }
        return adsByZone;
      },
      getAdsByAdZone(searchParameters, adZone) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        const ads = self.adQueries.get(queryIdentifier);
        let adsByZone = null;
        if (ads) {
          adsByZone = ads.filter((ad) => {
            return ad.zones.indexOf(adZone) !== -1;
          });
        }
        return adsByZone;
      },
      getAdsBySearchParameters(searchParameters) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        return self.adQueries.get(queryIdentifier);
      },
      getAdQueryStateBySearchParameters(searchParameters) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        return self.adQueryStates.get(queryIdentifier);
      },
    };
  });

export default AdStore;
